import { useRef, useState, useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import { useSpin } from "@/lib/spin";
import SpinStage from "@/components/SpinStage";
import { drawFrankenstein, FRANK_COLUMNS } from "@/lib/frankenstein";
import { Button } from "@/components/ui/button";
import { Shuffle, Maximize2, X } from "lucide-react";
import confetti from "canvas-confetti";

export default function FrankensteinSpinner({ compact = false }) {
  const { sensoryFriendly } = useSettings();
  const { display, spinning, countdown, run } = useSpin(sensoryFriendly);
  const [projector, setProjector] = useState(false);

  const prevSpinning = useRef(false);
  useEffect(() => {
    if (prevSpinning.current && !spinning && display && !sensoryFriendly) {
      const colors = ["#920e12", "#ffdedf", "#bf6163", "#550001", "#ffffff"];
      confetti({ particleCount: 140, spread: 90, startVelocity: 42, origin: { y: 0.6 }, colors });
    }
    prevSpinning.current = spinning;
  }, [spinning, display, sensoryFriendly]);

  const draw = () => {
    const res = drawFrankenstein();
    run(res, res.spin_pool);
  };

  if (projector) {
    return (
      <div className="fixed inset-0 z-50 stage-scrim text-white flex flex-col items-center justify-center p-8" data-testid="frank-projector">
        <button onClick={() => setProjector(false)} className="absolute top-6 right-6 text-[#dfd8da] hover:text-white" data-testid="frank-projector-exit"><X size={32} /></button>
        {display ? (
          <div className="text-center"><SpinStage result={display} spinning={spinning} countdown={countdown} projector wheelItems={FRANK_COLUMNS} reducedMotion={sensoryFriendly} /></div>
        ) : (
          <p className="font-runner text-4xl text-[#dfd8da]">Press Spin to build a character</p>
        )}
        <Button onClick={draw} disabled={spinning} className="mt-16 h-16 px-10 text-2xl bg-[#920e12] hover:bg-[#bf6163] text-white font-black" data-testid="frank-projector-draw">
          <Shuffle className="mr-2" /> {spinning ? "Building…" : "Spin"}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="frankenstein-spinner">
      <div className="bg-white rounded-2xl border border-[#dfd8da] p-6 space-y-4">
        <p className="text-[#545454]">Spin to build a wild character — you'll get a <strong>Character</strong>, a <strong>physical move</strong>, a <strong>voice</strong>, and a <strong>prop</strong>. Act it out in the mirror or for the room!</p>
        <div className="flex gap-3">
          <Button onClick={draw} disabled={spinning} className="flex-1 h-12 bg-[#920e12] hover:bg-[#550001] text-white font-bold text-base" data-testid="frank-draw">
            <Shuffle className={`mr-2 ${spinning ? "animate-spin" : ""}`} size={18} /> {spinning ? "Building…" : "Spin a character"}
          </Button>
          <Button onClick={() => setProjector(true)} variant="outline" className="h-12 border-[#920e12] text-[#920e12]" data-testid="frank-projector-btn">
            <Maximize2 className="mr-2" size={18} /> Projector
          </Button>
        </div>
      </div>
      {display && (
        <div className={`mt-6 bg-[#221517] text-white rounded-2xl p-8 text-center transition-transform duration-200 ${spinning ? "scale-[1.02]" : ""}`} data-testid="frank-result">
          <SpinStage result={display} spinning={spinning} countdown={countdown} compact={compact} wheelItems={FRANK_COLUMNS} reducedMotion={sensoryFriendly} />
        </div>
      )}
    </div>
  );
}
